import { useMutation } from "@apollo/client";
import { setSignupOrigin } from "analytics/signupOrigin";
import trackEvent from "components/tracking/trackEvent";
import { usePlanGate } from "components/website/components/planGate";
import { useCallback, useEffect, useRef, useState } from "react";
import { buildCreateTripInput, CREATE_TRIP_FROM_ROUTE, PARTNER } from "./createTripFromRoute";
import { openTrip } from "./openTrip";
import { totalNights, type RouteStop } from "./plannerLogic";

/**
 * "Get this trip": turn a route into a trip in the visitor's own Stippl account, then open it.
 *
 * A signed-out visitor first gets the site's login / register modal (the same plan gate the guide pages use);
 * once they are in, the creation carries on by itself. A signed-in visitor goes straight through.
 */

export type GetTripStatus = "idle" | "working" | "error";

/**
 * Whether a Firebase session exists. Firebase is imported here, on demand, and not at the top of the file:
 * the server renders this page too, and server output never imports it (HomeMarketing does the same).
 * `onAuthStateChanged` answers once the stored session has been read, so a reload mid-visit is not "signed out".
 */
async function hasSession(): Promise<boolean> {
	const { auth } = await import("components/firebase");
	return new Promise((resolve) => {
		const off = auth.onAuthStateChanged((user) => {
			off();
			resolve(!!user);
		});
	});
}

interface Options {
	stops: RouteStop[];
	/** the trip's name: the ready-made route's own, or "My Mad Monkey route" */
	name: string;
	/** "builder", or the ready-made route's id */
	source: string;
}

export function useGetThisTrip({ stops, name, source }: Options) {
	const gate = usePlanGate("madmonkey_get_trip");
	const [createTrip] = useMutation(CREATE_TRIP_FROM_ROUTE);
	const [status, setStatus] = useState<GetTripStatus>("idle");
	const [signedIn, setSignedIn] = useState(false);
	const busy = useRef(false);

	const create = useCallback(async () => {
		if (busy.current) return;
		busy.current = true;
		setStatus("working");
		try {
			const { data } = await createTrip({ variables: { input: buildCreateTripInput(stops, name) } });
			const trip = data?.createTripFromRoute;
			if (!trip?._id) throw new Error("createTripFromRoute returned no trip");
			trackEvent("partner_trip_created", { partner: PARTNER, stops: stops.length, nights: totalNights(stops), source }, "marketing_site");
			// Leaves the page: the button stays busy until it does.
			openTrip(trip);
		} catch (e: any) {
			console.error("[MadMonkey] createTripFromRoute failed:", { source, error: e?.message || e });
			busy.current = false;
			setStatus("error");
		}
	}, [createTrip, stops, name, source]);

	const request = useCallback(async () => {
		if (busy.current || status === "working" || !stops.length) return;
		setStatus("working");
		let signedIn: boolean;
		try {
			signedIn = await hasSession();
		} catch (e: any) {
			// Firebase's chunk failed to load (an old chunk after a deploy, or offline): show the error, not a button stuck working.
			console.error("[MadMonkey] the session check failed:", e?.message || e);
			setStatus("error");
			return;
		}
		if (signedIn) {
			await create();
			return;
		}
		setStatus("idle");
		// So the signup this opens can say it started on this page (analytics/signupOrigin).
		setSignupOrigin("madmonkey", source);
		gate.requestPlan();
	}, [status, stops.length, create, source, gate]);

	// After the modal signs the visitor in, the creation continues by itself. LoginModal reports the sign-in while
	// it renders, so the work waits for the next effect instead of starting there.
	useEffect(() => {
		if (!signedIn) return;
		setSignedIn(false);
		void create();
	}, [signedIn, create]);

	const onLogin = useCallback(() => setSignedIn(true), []);

	return { request, status, gate, onLogin };
}
